import { countryCodeToName, timezoneToCountryCode } from "@/utils/timezoneToCountry";

/**
 * Feriados públicos reales del país de la psicóloga, vía la API pública de Nager.Date (gratis,
 * sin credenciales). El país sale de la zona horaria del perfil. Si no hay país detectable o la
 * API falla, no hay feriados (nunca se inventa uno). Lo usan el aviso de Comunidad y el de la
 * Agenda; la lógica de qué feriados corresponden a cada país es la de Nager, no la nuestra.
 */
export interface UpcomingHoliday {
  /** YYYY-MM-DD — el día del feriado en el país. */
  date: string;
  localName: string;
}

/** Desde cuántos días antes se avisa un feriado: Comunidad, la Agenda y (fase 2) las notificaciones. */
export const HOLIDAY_NOTICE_DAYS = 14;

/** Una consulta por país por sesión: Comunidad y la Agenda comparten la respuesta. */
const cache = new Map<string, Promise<UpcomingHoliday[]>>();

export function fetchPublicHolidays(countryCode: string): Promise<UpcomingHoliday[]> {
  let p = cache.get(countryCode);
  if (!p) {
    p = fetch(`https://date.nager.at/api/v3/NextPublicHolidays/${countryCode}`)
      .then((resp) => (resp.ok ? (resp.json() as Promise<UpcomingHoliday[]>) : []))
      .catch(() => [] as UpcomingHoliday[]);
    // Si falló, que se pueda reintentar más tarde.
    p.then((list) => { if (list.length === 0) cache.delete(countryCode); });
    cache.set(countryCode, p);
  }
  return p;
}

/** "Hoy" (YYYY-MM-DD) en una zona horaria. */
export function todayInTimezone(timezone: string, now = new Date()): string {
  try {
    return now.toLocaleDateString("en-CA", { timeZone: timezone });
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Suma días a una fecha YYYY-MM-DD (sin husos: es aritmética de calendario). */
export function addDaysIso(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * Feriados desde HOY (en la zona horaria de la psicóloga, incluido el mismo día del feriado)
 * hasta `days` días adelante, ordenados por fecha.
 */
export function holidaysWithinDays(
  holidays: UpcomingHoliday[],
  days: number,
  timezone: string,
  now = new Date(),
): UpcomingHoliday[] {
  const today = todayInTimezone(timezone, now);
  const limit = addDaysIso(today, days);
  return holidays
    .filter((h) => h.date >= today && h.date <= limit)
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** País de la psicóloga, por nombre ("Argentina"), a partir de su zona horaria. null si no se
 *  puede determinar — el que llama decide el texto de reemplazo. */
export function countryNameFromTimezone(timezone: string): string | null {
  const code = timezoneToCountryCode(timezone);
  if (!code) return null;
  const known = countryCodeToName(code);
  if (known) return known;
  try {
    return new Intl.DisplayNames(["es"], { type: "region" }).of(code) ?? null;
  } catch {
    return null;
  }
}

/** "lunes 02/11" para una fecha YYYY-MM-DD (día de calendario, sin corrimiento por huso). */
export function formatHolidayDay(dateIso: string): string {
  const weekday = new Intl.DateTimeFormat("es-AR", { weekday: "long", timeZone: "UTC" }).format(
    new Date(`${dateIso}T12:00:00Z`),
  );
  const [, m, d] = dateIso.split("-");
  return `${weekday} ${d}/${m}`;
}
